import Close from '@mui/icons-material/Close';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormHelperText,
	Grid,
	IconButton,
	Link,
	Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import { YESNO } from '@/common/constants/common';
import {
	useGetDicListQuery,
	useGetFamilyCarersMemberListQuery,
} from '@/api/common';
import { useCreateFamilyCarerMutation } from '@/api/member-management/admissionRequest';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	open: boolean;
	userId: string;
	onClose: () => void;
	onCreated: (newCarerName: string) => void;
}

export default function AddFamilyCarerDialog({
	open,
	userId,
	onClose,
	onCreated,
}: Props) {
	const { t } = useTranslation();
	const [submitting, setSubmitting] = useState(false);

	const [createFamilyCarer] = useCreateFamilyCarerMutation();

	const { data: relationshipDicList } = useGetDicListQuery('relationship', {
		refetchOnMountOrArgChange: true,
		skip: !open,
	});

	// Only one carer per member may be the emergency contact, so the option is
	// taken away when somebody already holds it instead of letting the request
	// come back as a 400.
	const { data: existingCarers } = useGetFamilyCarersMemberListQuery(userId, {
		refetchOnMountOrArgChange: true,
		skip: !open || !userId,
	});
	const emergencyContactTaken = (existingCarers?.data || []).some(
		(item) => !!item.emergencyContact
	);

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		familyCarerName: Yup.string().required(t('errorMessage.pleaseEnter')),
		age: Yup.string().nullable(),
		address: Yup.string().required(t('errorMessage.pleaseEnter')),
		telNo: Yup.string()
			.required(t('errorMessage.pleaseEnter'))
			.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
		relationship: Yup.string().required(t('errorMessage.pleaseSelect')),
		emergencyContact: Yup.string().required(t('errorMessage.pleaseSelect')),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	useEffect(() => {
		if (!open) {
			methods.reset({});
		} else if (emergencyContactTaken) {
			// "No" is the only answer left, so fill it in rather than asking the
			// user to pick from a group whose other option is disabled.
			methods.setValue('emergencyContact', 'false');
		}
	}, [open, emergencyContactTaken]);

	const handleClose = () => {
		methods.reset({});
		onClose();
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		if (!userId || submitting) return;
		setSubmitting(true);
		try {
			const response = await createFamilyCarer({
				userId,
				familyCarerName: data.familyCarerName,
				// Age is optional; an empty text field must not be sent as ''.
				age: data.age ? data.age : null,
				relationship: data.relationship,
				address: data.address,
				telNo: data.telNo,
				// The radio group hands back the option id as a string.
				emergencyContact: String(data.emergencyContact) === 'true',
			});

			if ('data' in response) {
				enqueueSnackbar(t('snackbar.createFamilyCarerSuccess'), {
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				});

				// The involving fields snapshot the carer's name, so hand the name
				// back to be selected straight away. Invalidating FamilyCarersList
				// refreshes the dropdown behind this dialog with the same name.
				if (response.data?.data?.id) onCreated(data.familyCarerName);

				handleClose();
			}
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			aria-labelledby='add-family-carer-dialog-title'
			maxWidth='sm'
			fullWidth
			onKeyDown={(e) => {
				if (e.key === 'Tab') {
					e.stopPropagation();
				}
			}}
		>
			<form onSubmit={methods.handleSubmit(onSubmit)}>
				<DialogTitle variant='h5' id='add-family-carer-dialog-title'>
					{t('button.newFamilyCarer')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('case.addFamilyCarerText')}{' '}
						{userId ? (
							<>
								{/* Opens in a new tab so the in-progress intervention form
									    behind this dialog is not discarded. */}
								<Link
									href={`/member-management/member-list/detail/${userId}`}
									target='_blank'
									rel='noopener noreferrer'
								>
									{t('preAdmission.familyCarersInformation')}
									<OpenInNewIcon
										sx={{
											fontSize: '14px',
											marginLeft: '2px',
											verticalAlign: 'text-bottom',
										}}
									/>
								</Link>
								.
							</>
						) : (
							`${t('preAdmission.familyCarersInformation')}.`
						)}
					</Typography>
					<FormProvider {...methods}>
						<Grid container spacing={3} marginTop={0} marginBottom={2}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='familyCarerName'
									label={t('preAdmission.name') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='age'
									label={t('preAdmission.age')}
								/>
							</Grid>
							<Grid size={6}>
								<AppAutoCompleteMenu
									control={methods.control}
									name='relationship'
									label={t('preAdmission.relationship') + '*'}
									options={(relationshipDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='address'
									label={t('preAdmission.address') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='telNo'
									label={t('preAdmission.telNo') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.emergencyContact') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='emergencyContact'
									row
									options={YESNO}
									disabledOptions={emergencyContactTaken ? [true] : []}
								/>
								{emergencyContactTaken && (
									<FormHelperText sx={{ paddingLeft: '1rem' }}>
										{t('case.emergencyContactAlreadyExists')}
									</FormHelperText>
								)}
							</Grid>
						</Grid>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						disabled={submitting}
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
